import React, { useEffect, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Input,
  Modal,
  ModalHeader,
  Row,
  Col,
} from "reactstrap"
import { useNavigate } from "react-router-dom"
import { FaEdit, FaPlus, FaEye, FaTrashAlt } from "react-icons/fa"
import Configs from "../../config"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import Loader from "components/Loader/Loader"
import {
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Label,
} from "reactstrap"
import classnames from "classnames"
import Swal from "sweetalert2"
import { MdAdd, MdCancel, MdDelete, MdSave } from "react-icons/md"
import moment from "moment"

const getNearestNextFollowUpDate = (followUps = []) => {
  if (!followUps.length) return "-"

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const upcoming = followUps
    .filter(f => f.nextFollowUpDate)
    .map(f => ({
      ...f,
      nextDateObj: new Date(f.nextFollowUpDate),
    }))
    .filter(f => f.nextDateObj >= today)
    .sort((a, b) => a.nextDateObj - b.nextDateObj)

  if (!upcoming.length) return "-"

  return moment(upcoming[0].nextFollowUpDate).format("DD-MM-YYYY")
}

const LeadsIndex = () => {
  document.title = "Leads | Vintage Infra Equipment Sol PVT LTD"
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [leads, setLeads] = useState([])

  const [followUpModal, setFollowUpModal] = useState(false)
  const [activeTab, setActiveTab] = useState("add")
  const [selectedLeadId, setSelectedLeadId] = useState(null)

  const [leadTypeFilter, setLeadTypeFilter] = useState("")

  const [followUpForm, setFollowUpForm] = useState({
    date: "",
    nextFollowUpDate: "",
    description: "",
  })

  const [followUpHistory, setFollowUpHistory] = useState([])

  useEffect(() => {
    fetchLeads()
  }, [])

  async function fetchLeads() {
    const APIdata = await fetchData(setLoading, "leads")
    if (APIdata?.success) {
      setLeads(APIdata?.data)
    }
  }

  const [searchTerm, setSearchTerm] = useState("")

  const filteredLeads = leads.filter(lead => {
    // 🔹 Search filter
    const matchesSearch = Object.values({
      company: lead.company,
      contact: lead.contactPersonName,
      email: lead.contactPersonEmail,
      phone: lead.contactPersonPhone,
      source: lead.source,
      leadType: lead.leadType,
      status: lead.status,
      assignedStaff: lead.assignedStaff,
    })
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())

    // 🔹 Badge filter
    const matchesLeadType = !leadTypeFilter || lead.leadType === leadTypeFilter

    return matchesSearch && matchesLeadType
  })

  /* ================= Pagination ================= */
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const totalPages = Math.ceil(filteredLeads.length / pageSize)
  const start = (currentPage - 1) * pageSize
  const paginatedLeads = filteredLeads.slice(start, start + pageSize)

  const getTodayDate = () => {
    const today = new Date()
    return today.toISOString().split("T")[0] // YYYY-MM-DD
  }

  const handleFollowUp = async lead => {
    setSelectedLeadId(lead?.id)
    setActiveTab("add")
    setFollowUpHistory(lead?.followUps || [])
    setFollowUpForm({
      date: getTodayDate(),
      nextFollowUpDate: "",
      description: "",
    })
    setFollowUpModal(true)
  }

  const handleFollowUpChange = e => {
    setFollowUpForm({
      ...followUpForm,
      [e.target.name]: e.target.value,
    })
  }

  const saveFollowUp = async () => {
    if (!followUpForm.date) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select Follow Up Date",
      })
      return
    }

    if (!followUpForm.description.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please enter Description",
      })
      return
    }

    if (
      followUpForm.nextFollowUpDate &&
      followUpForm.nextFollowUpDate < followUpForm.date
    ) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Date",
        text: "Next Follow Up Date cannot be before Follow Up Date",
      })
      return
    }

    const payload = {
      ...followUpForm,
      leadId: selectedLeadId,
    }

    if (!payload.nextFollowUpDate) {
      delete payload.nextFollowUpDate
    }

    const APIdata = await FetchPostData(setLoading, "leads/follow/up", payload)

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "FollowUp Added Successfully",
        text: APIdata?.data?.message,
        icon: "success",
      })

      setFollowUpHistory(APIdata?.data?.data)

      setFollowUpForm({
        date: "",
        nextFollowUpDate: "",
        description: "",
      })

      setActiveTab("history")
      await fetchLeads()
    } else {
      Swal.fire({
        title: "Error Adding FollowUp",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const deleteFollowUp = async id => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This follow-up will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) return

    const APIdata = await FetchDeleteData(setLoading, "leads/follow/up", id)

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Deleted!",
        text: APIdata?.data?.message || "Follow-up deleted successfully.",
        icon: "success",
      })
      setFollowUpHistory(APIdata?.data?.data)
    } else {
      Swal.fire({
        title: "Error",
        text: APIdata?.response?.data?.message || "Failed to delete follow-up.",
        icon: "error",
      })
    }
  }

  const handleStatusChange = async (id, value) => {
    const APIdata = await FetchPutData(setLoading, "leads", id, {
      status: value,
    })

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Lead Status Updated Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      fetchLeads()
    } else {
      Swal.fire({
        title: "Error Updating Lead Status",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleLeadChange = async (id, value) => {
    const APIdata = await FetchPutData(setLoading, "leads", id, {
      leadType: value,
    })

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Lead Type Updated Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      fetchLeads()
    } else {
      Swal.fire({
        title: "Error Updating Lead Type",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Leads Management" breadcrumbItem="Lead" />

      <Card>
        <CardBody>
          {/* ================= Top Actions ================= */}
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <Input
                type="select"
                className="rounded-4"
                value={pageSize}
                style={{ width: 90 }}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                placeholder="Search leads..."
                className="rounded-4"
                style={{ width: 250 }}
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
              />

              <div className="">
                <Badge
                  color="danger"
                  role="button"
                  className="me-2 p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("hot_1_month")
                    setCurrentPage(1)
                  }}
                >
                  Hot: {leads.filter(i => i.leadType === "hot_1_month").length}
                </Badge>

                <Badge
                  color="warning"
                  role="button"
                  className="me-2 p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("warm_2_month")
                    setCurrentPage(1)
                  }}
                >
                  Warm:{" "}
                  {leads.filter(i => i.leadType === "warm_2_month").length}
                </Badge>

                <Badge
                  color="info"
                  role="button"
                  className="me-2 p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("cold_3_month")
                    setCurrentPage(1)
                  }}
                >
                  Cold:{" "}
                  {leads.filter(i => i.leadType === "cold_3_month").length}
                </Badge>

                <Badge
                  color="secondary"
                  role="button"
                  className="me-2 p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("normal_enquiry")
                    setCurrentPage(1)
                  }}
                >
                  Normal:{" "}
                  {leads.filter(i => i.leadType === "normal_enquiry").length}
                </Badge>

                <Badge
                  color="success"
                  role="button"
                  className="p-2 fs-6"
                  onClick={() => {
                    setLeadTypeFilter("extreme_hot_10_days")
                    setCurrentPage(1)
                  }}
                >
                  Extremely Hot:{" "}
                  {
                    leads.filter(i => i.leadType === "extreme_hot_10_days")
                      .length
                  }
                </Badge>
                {leadTypeFilter && (
                  <Button
                    color="dark"
                    size="sm"
                    className="ms-2"
                    onClick={() => {
                      setLeadTypeFilter("")
                      setCurrentPage(1)
                    }}
                  >
                    <MdCancel />
                  </Button>
                )}
              </div>
            </div>

            <Button
              className="btn-blue rounded-4 px-3"
              onClick={() => navigate(`${Configs.appurl}leads/add`)}
            >
              + Lead
            </Button>
          </div>

          {/* ================= Table ================= */}
          <Table bordered hover responsive>
            <thead className="table-dark">
              <tr>
                <th>ID</th>
                <th>Company</th>
                <th>Contact Person</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Source</th>
                <th>Lead Type</th>
                <th>Status</th>
                <th>Assigned</th>
                <th>Next Follow Up</th>
                <th style={{ minWidth: "12%" }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedLeads.length === 0 && (
                <tr>
                  <td colSpan="11" className="text-center text-muted py-4">
                    No leads found
                  </td>
                </tr>
              )}

              {paginatedLeads.map((lead, index) => (
                <tr key={lead.id}>
                  <td>{start + index + 1}</td>
                  <td>{lead.company}</td>
                  <td>{lead.contactPersonName}</td>
                  <td>{lead.contactPersonEmail}</td>
                  <td>{lead.contactPersonPhone}</td>
                  <td>{lead.source}</td>

                  <td>
                    <Input
                      type="select"
                      name="leadType"
                      value={lead.leadType || ""}
                      onChange={e => {
                        handleLeadChange(lead?.id, e.target.value)
                      }}
                      className="form-select"
                    >
                      <option value="">Select</option>
                      <option value="hot_1_month">Hot (1 Month)</option>
                      <option value="warm_2_month">Warm (2 Month)</option>
                      <option value="cold_3_month">Cold (3 Month)</option>
                      <option value="normal_enquiry">Normal (Enquiry)</option>
                      <option value="extreme_hot_10_days">
                        Extreme Hot (10 days)
                      </option>
                    </Input>
                  </td>

                  <td>
                    <Input
                      type="select"
                      name="status"
                      value={lead.status || ""}
                      onChange={e => {
                        handleStatusChange(lead?.id, e.target.value)
                      }}
                      className="form-select"
                    >
                      <option value="">Select</option>
                      <option value="new">New</option>
                      <option value="introduction">Introduction</option>
                      <option value="meeting">Meeting</option>
                      <option value="document_collected">
                        Document Collected
                      </option>
                      <option value="file_login">File Login</option>
                      <option value="lost">Lost</option>
                    </Input>
                  </td>

                  <td>{lead?.user?.name}</td>
                  <td>{getNearestNextFollowUpDate(lead?.followUps)}</td>

                  <td>
                    <Button
                      title="View"
                      size="sm"
                      color="secondary"
                      className="me-1"
                      onClick={() =>
                        navigate(`${Configs.appurl}leads/view/${lead.id}`)
                      }
                    >
                      <FaEye />
                    </Button>

                    <Button
                      size="sm"
                      color="success"
                      title="Edit"
                      className="me-1"
                      onClick={() =>
                        navigate(`${Configs.appurl}leads/edit/${lead.id}`)
                      }
                    >
                      <FaEdit />
                    </Button>

                    <Button
                      size="sm"
                      color="primary"
                      title="Follow Up"
                      onClick={() => {
                        handleFollowUp(lead)
                      }}
                    >
                      <FaPlus />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* ================= Pagination ================= */}
          <nav className="d-flex justify-content-between mt-3">
            <h6>
              Showing {filteredLeads.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredLeads.length)} of{" "}
              {filteredLeads.length} entries
            </h6>

            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal
        isOpen={followUpModal}
        toggle={() => setFollowUpModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setFollowUpModal(false)}>
          Lead Follow Up
        </ModalHeader>

        <ModalBody>
          {/* ================= Tabs ================= */}
          <Nav tabs>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "add" })}
                onClick={() => setActiveTab("add")}
                style={{ cursor: "pointer" }}
              >
                Add Follow Up
              </NavLink>
            </NavItem>

            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "history" })}
                onClick={() => setActiveTab("history")}
                style={{ cursor: "pointer" }}
              >
                History
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="mt-3">
            {/* ================= ADD FOLLOW UP ================= */}
            <TabPane tabId="add">
              <Form>
                <Row>
                  <Col xl={6}>
                    <FormGroup>
                      <Label>
                        Follow Up Date <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="date"
                        name="date"
                        value={followUpForm.date}
                        onChange={handleFollowUpChange}
                        className="form-control rounded-4"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col xl={6}>
                    <FormGroup>
                      <Label>Next Follow Up Date</Label>
                      <Input
                        type="date"
                        name="nextFollowUpDate"
                        value={followUpForm.nextFollowUpDate}
                        onChange={handleFollowUpChange}
                        className="form-control rounded-4"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label>
                    Description <span className="text-danger">*</span>{" "}
                  </Label>
                  <Input
                    type="textarea"
                    rows={10}
                    name="description"
                    value={followUpForm.description}
                    onChange={handleFollowUpChange}
                    className="form-control rounded-4"
                    required
                  />
                </FormGroup>

                <div className="d-flex justify-content-end">
                  <Button
                    type="button"
                    className="btn-blue"
                    onClick={saveFollowUp}
                  >
                    <MdAdd />  Follow Up
                  </Button>
                </div>
              </Form>
            </TabPane>

            {/* ================= HISTORY ================= */}
            <TabPane tabId="history">
              {followUpHistory?.length === 0 ? (
                <p className="text-muted text-center">
                  No follow up history available
                </p>
              ) : (
                <Table bordered striped>
                  <thead className="table-dark">
                    <tr>
                      <th>Follow Up Date</th>
                      <th>Next Follow Up</th>
                      <th>Description</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followUpHistory.reverse()?.map(item => (
                      <tr key={item.id}>
                        <td>{moment(item.date).format("DD-MM-YYYY")}</td>
                        <td>
                          {item.nextFollowUpDate
                            ? moment(item.nextFollowUpDate).format("DD-MM-YYYY")
                            : "-"}
                        </td>
                        <td>{item.description}</td>
                        <td>
                          <Button
                            size="sm"
                            color="danger"
                            onClick={() => deleteFollowUp(item.id)}
                          >
                            <FaTrashAlt />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </TabPane>
          </TabContent>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default LeadsIndex
